import React, { useState, lazy, Component } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle, AlertCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/Button';
export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    // Simulate API call
    setTimeout(() => {
      setStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      setTimeout(() => setStatus('idle'), 3000);
    }, 1000);
  };
  const whatsappMessage = encodeURIComponent('Hello Maisha Medical Center. I have a general inquiry.');
  return <div className="min-h-screen bg-brand-light pb-20" data-id="element-595">
      {/* Hero Band */}
      <div className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-596">
        <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-597">
          <span className="text-brand-gold font-semibold tracking-wider text-sm uppercase mb-4 block" data-id="element-598">
            Get In Touch
          </span>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6" data-id="element-599">
            Contact Us
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed" data-id="element-600">
            We are here to assist you with any inquiries, appointment bookings,
            or medical concerns. Reach out to us today.
          </p>
        </div>
      </div>

      <div className="max-w-brand mx-auto px-4 sm:px-6 lg:px-8 mt-16" data-id="element-601">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-602">
          {/* Left Column: Form & Emergency */}
          <div className="space-y-8" data-id="element-603">
            {/* Emergency Block */}
            <div className="bg-red-50 border-l-4 border-brand-red p-6 rounded-r-2xl shadow-sm" data-id="element-604">
              <div className="flex items-center gap-3 mb-2" data-id="element-605">
                <AlertCircle className="w-6 h-6 text-brand-red" data-id="element-606" />
                <h3 className="font-heading font-bold text-lg text-brand-navy" data-id="element-607">
                  Emergency Contact
                </h3>
              </div>
              <p className="text-slate-700 text-sm mb-3" data-id="element-608">
                For urgent medical assistance during our operating hours, please
                call our emergency line immediately.
              </p>
              <a href="tel:+2676540244" className="text-xl font-bold text-brand-red hover:underline" data-id="element-609">
                +267 6540244
              </a>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-2xl shadow-soft border border-slate-100" data-id="element-610">
              <h2 className="text-2xl font-heading font-bold text-brand-navy mb-6" data-id="element-611">
                Send us a message
              </h2>

              <form onSubmit={handleSubmit} className="space-y-5" data-id="element-612">
                <div data-id="element-613">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-614">
                    Full Name *
                  </label>
                  <input required type="text" value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy transition-all" placeholder="John Doe" data-id="element-615" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-616">
                  <div data-id="element-617">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-618">
                      Email Address *
                    </label>
                    <input required type="email" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy transition-all" placeholder="john@example.com" data-id="element-619" />
                  </div>
                  <div data-id="element-620">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-621">
                      Phone Number
                    </label>
                    <input type="tel" value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy transition-all" placeholder="+267 XXX XXXX" data-id="element-622" />
                  </div>
                </div>
                <div data-id="element-623">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-624">
                    Message *
                  </label>
                  <textarea required rows={4} value={formData.message} onChange={e => setFormData({
                  ...formData,
                  message: e.target.value
                })} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy transition-all resize-none" placeholder="How can we help you?" data-id="element-625" />
                </div>
                <Button type="submit" variant="primary" fullWidth disabled={status === 'submitting' || status === 'success'} icon={status === 'success' ? undefined : <Send className="w-4 h-4" data-id="element-627" />} data-id="element-626">
                  {status === 'submitting' ? 'Sending...' : status === 'success' ? 'Message Sent!' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>

          {/* Right Column: Contact Details & Map */}
          <div className="space-y-8" data-id="element-628">
            {/* Details Grid */}
            <div className="bg-white p-8 rounded-2xl shadow-soft border border-slate-100" data-id="element-629">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-630">
                <div className="flex items-start gap-4" data-id="element-631">
                  <div className="w-12 h-12 bg-brand-lighter rounded-xl flex items-center justify-center text-brand-gold shrink-0" data-id="element-632">
                    <MapPin className="w-6 h-6" data-id="element-633" />
                  </div>
                  <div data-id="element-634">
                    <h3 className="font-heading font-bold text-brand-navy mb-1" data-id="element-635">
                      Location
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed" data-id="element-636">
                      Tsabong, Botswana
                      <br data-id="element-637" />
                      (Full street address here)
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-638">
                  <div className="w-12 h-12 bg-brand-lighter rounded-xl flex items-center justify-center text-brand-gold shrink-0" data-id="element-639">
                    <Clock className="w-6 h-6" data-id="element-640" />
                  </div>
                  <div data-id="element-641">
                    <h3 className="font-heading font-bold text-brand-navy mb-1" data-id="element-642">
                      Hours
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed" data-id="element-643">
                      Mon - Fri: 8am - 6pm
                      <br data-id="element-644" />
                      Sat: 9am - 1pm
                      <br data-id="element-645" />
                      Sun: Closed
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-646">
                  <div className="w-12 h-12 bg-brand-lighter rounded-xl flex items-center justify-center text-brand-gold shrink-0" data-id="element-647">
                    <Phone className="w-6 h-6" data-id="element-648" />
                  </div>
                  <div data-id="element-649">
                    <h3 className="font-heading font-bold text-brand-navy mb-1" data-id="element-650">
                      Phone
                    </h3>
                    <a href="tel:+2676540244" className="text-slate-600 text-sm hover:text-brand-red transition-colors block" data-id="element-651">
                      +267 6540244
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-652">
                  <div className="w-12 h-12 bg-brand-lighter rounded-xl flex items-center justify-center text-brand-gold shrink-0" data-id="element-653">
                    <Mail className="w-6 h-6" data-id="element-654" />
                  </div>
                  <div data-id="element-655">
                    <h3 className="font-heading font-bold text-brand-navy mb-1" data-id="element-656">
                      Email
                    </h3>
                    <a href="mailto:bookings@maishahealth.co.bw" className="text-slate-600 text-sm hover:text-brand-red transition-colors block" data-id="element-657">
                      bookings@maishahealth.co.bw
                    </a>
                  </div>
                </div>
              </div>

              <hr className="my-8 border-slate-100" data-id="element-658" />

              <div className="text-center" data-id="element-659">
                <p className="text-sm text-slate-600 mb-4" data-id="element-660">
                  Prefer to chat? We are available on WhatsApp.
                </p>
                <Button href={`https://wa.me/26773241613?text=${whatsappMessage}`} target="_blank" variant="outline" icon={<MessageCircle className="w-5 h-5 text-[#25D366]" data-id="element-662" />} className="border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-brand-navy" data-id="element-661">
                  Chat on WhatsApp
                </Button>
              </div>
            </div>

            {/* Google Map — Tsabong, Botswana */}
            <div className="rounded-2xl h-64 w-full overflow-hidden shadow-inner border border-slate-300" data-id="element-663">
              <iframe title="Map of Tsabong, Botswana" src="https://www.google.com/maps?q=Tsabong,Botswana&z=13&output=embed" className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen data-id="element-664" />
            </div>
          </div>
        </div>
      </div>
    </div>;
}